import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import Bouton from '../../UI/Bouton/Bouton';
import * as type_btn from '../../../ressources/glossaires/glossaire_type_de_bouton';
import * as actions from 'services/actions';
import {
  AiOutlinePlus,
  AiOutlineCheck,
  AiOutlineEdit,
  AiOutlineClose,
  AiOutlineStar,
} from 'react-icons/ai';
import S from './PresetFavoriteProjects.module.scss';

interface RootState {
  authentifie: {
    filtre_projects_list_id: number[];
  };
  maintenance: {
    projects_list: Array<{ id: number; name: string }>;
  };
}

const PresetFavoriteProjects: React.FC = () => {
  const [presets, setPresets] = useState<{ [key: string]: string }>({});
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const filtre_projects_list_id = useSelector(
    (state: RootState) => state.authentifie.filtre_projects_list_id
  );

  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Load presets from localStorage
    const savedPresets = localStorage.getItem('maintenanceProjectPresets');
    if (savedPresets) {
      setPresets(JSON.parse(savedPresets));
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const renamePreset = (oldName: string) => {
    const newName = prompt(checkMot('Renomez votre preset'), oldName);
    if (newName && newName !== oldName) {
      const newPresets = { ...presets };
      newPresets[newName] = newPresets[oldName];
      delete newPresets[oldName];
      setPresets(newPresets);
      localStorage.setItem(
        'maintenanceProjectPresets',
        JSON.stringify(newPresets)
      );
      if (activePreset === oldName) {
        setActivePreset(newName);
      }
    }
  };
  const saveCurrentAsPreset = () => {
    const presetName = prompt(checkMot('Entrez le nom de votre preset'));
    if (presetName) {
      const newPresets = {
        ...presets,
        [presetName]: JSON.stringify(filtre_projects_list_id),
      };

      setPresets(newPresets);
      localStorage.setItem(
        'maintenanceProjectPresets',
        JSON.stringify(newPresets)
      );
    }
  };
  const loadPreset = (presetName: string) => {
    const preset = presets[presetName];
    if (preset) {
      const projects = JSON.parse(preset);
      // Update Redux
      dispatch(actions.gererFiltreParProjet(projects));
      // Update localStorage with comma-separated IDs
      localStorage.setItem('local_filtre_projects_list_id', projects.join(','));
      setActivePreset(presetName);
    }
  };

  const deletePreset = (presetName: string) => {
    if (
      window.confirm(
        checkMot(`Confirmer la suppression du preset ${presetName} ?`)
      )
    ) {
      const newPresets = { ...presets };
      delete newPresets[presetName];
      setPresets(newPresets);
      localStorage.setItem(
        'maintenanceProjectPresets',
        JSON.stringify(newPresets)
      );
      if (activePreset === presetName) {
        setActivePreset(null);
      }
    }
  };
  return (
    <div className={S.container} ref={containerRef}>
      <Bouton
        type={type_btn.outil}
        rond={true}
        cliquer={() => setIsOpen(!isOpen)}
        overText={checkMot('manage_presets')}
      >
        <AiOutlineStar size={15} />
      </Bouton>{' '}
      {isOpen && (
        <div className={S.presetsList}>
          <div className={S.presetsHeader}>
            <h4>
              {Object.keys(presets).length > 0
                ? checkMot('Preset sauvegardé')
                : checkMot('Aucun preset')}
            </h4>
            <div className={S.headerButtons}>
              <Bouton
                type={type_btn.outil}
                rond={true}
                petit={true}
                cliquer={saveCurrentAsPreset}
                overText={checkMot('Enregistrer le preset actuel')}
              >
                <AiOutlinePlus size={15} />
              </Bouton>
              <Bouton
                type={type_btn.outil}
                rond={true}
                petit={true}
                cliquer={() => setIsOpen(false)}
                overText={checkMot('Fermer la liste des presets')}
              >
                <AiOutlineClose size={15} />
              </Bouton>
            </div>
          </div>
          {Object.keys(presets).map((presetName) => (
            <div key={presetName} className={S.presetItem}>
              <div className={S.presetHeader}>
                <Bouton
                  type={type_btn.outil}
                  rond={true}
                  cliquer={() => loadPreset(presetName)}
                  className={activePreset === presetName ? S.active : ''}
                  overText={presetName}
                >
                  <AiOutlineCheck size={15} />
                </Bouton>
                <span className={S.presetName}>{presetName}</span>
                <div className={S.presetActions}>
                  <Bouton
                    type={type_btn.outil}
                    rond={true}
                    petit={true}
                    cliquer={() => renamePreset(presetName)}
                    overText={checkMot('rename_preset')}
                  >
                    <AiOutlineEdit size={12} />
                  </Bouton>
                  <Bouton
                    type={type_btn.danger}
                    rond={true}
                    petit={true}
                    cliquer={() => deletePreset(presetName)}
                    overText={checkMot('delete_preset')}
                  >
                    ×
                  </Bouton>
                </div>
              </div>
              <div className={S.presetProjects}>
                <div className={S.projectsList}>
                  {JSON.parse(presets[presetName]).map(
                    (projectId: number, index: number, array: number[]) => (
                      <span className={S.projectId}>
                        {projectId}
                        {index < array.length - 1 && (
                          <span className={S.separator}>,</span>
                        )}
                      </span>
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PresetFavoriteProjects;
